import type { EventPage } from '../../services/event/fetchEventPage'

/** What one paginated event stream stores under its cache key. */
export interface EventStreamData {
  pages: EventPage[]
}

/**
 * Whether the stream has pages left to load.
 *
 * Read off the LAST loaded page: it is the only one whose pagination describes
 * where the stream currently stands. Absent pagination means the response was
 * the whole answer — a bare array, or a server that reported nothing usable —
 * and is treated as complete rather than as "keep going", because stopping
 * early renders a short list while looping never terminates.
 */
export const hasMorePages = (
  pages: EventPage[] | null | undefined
): boolean => {
  const last = pages?.[pages.length - 1]?.pagination
  if (!last) {
    return false
  }
  const current = Number(last.current_page ?? 0)
  const total = Number(last.total_pages ?? 0)
  return current > 0 && current < total
}

/**
 * The page number to request next, or null when the stream is done.
 *
 * Derived from the response's own `current_page` rather than the number of
 * pages held, so a server that answers a request with a different page than it
 * was asked for still advances from where it actually is.
 */
export const nextPageNumber = (
  data: EventStreamData | null | undefined
): number | null => {
  const pages = data?.pages
  if (!hasMorePages(pages)) {
    return null
  }
  const last = pages?.[pages.length - 1]?.pagination
  return Number(last?.current_page ?? 0) + 1
}

/**
 * Appends a freshly fetched page to the stream.
 *
 * Refuses a page the stream already holds. Two scroll events can fire a request
 * each before either lands, and appending both would duplicate every row on
 * that page — visible as the same game or event twice in the list.
 */
export const appendEventPage = (
  data: EventStreamData | null | undefined,
  page: EventPage
): EventStreamData => {
  const pages = data?.pages ?? []
  const incoming = Number(page?.pagination?.current_page ?? NaN)

  if (Number.isFinite(incoming)) {
    const alreadyHeld = pages.some(
      (held) => Number(held?.pagination?.current_page ?? NaN) === incoming
    )
    if (alreadyHeld) {
      return { pages }
    }
  }

  return { pages: [...pages, page] }
}
